import { useState } from 'react';

import ChatInput from './ChatInput';
import WelcomeSection from './welocmeSection';
import { submitContent } from '../../services/dashboardService';

interface Message {
  type: 'user' | 'bot';
  content: string;
}

const renderInlineContent = (text: string) => {
  const parts = text.split(/(\*\*.*?\*\*|`.*?`)/g);

  return parts.map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={index}>{part.slice(2, -2)}</strong>;
    }

    if (part.startsWith('`') && part.endsWith('`')) {
      return (
        <code key={index} className="rounded bg-white px-1 py-0.5 text-xs text-gray-700">
          {part.slice(1, -1)}
        </code>
      );
    }

    return part;
  });
};

const renderBotContent = (content: string) => {
  const blocks = content.split(/```/g);

  return blocks.map((block, blockIndex) => {
    const isCodeBlock = blockIndex % 2 === 1;

    if (isCodeBlock) {
      const code = block.replace(/^[a-zA-Z]+\n/, '').trim();

      return (
        <pre key={blockIndex} className="my-3 overflow-x-auto rounded bg-gray-900 p-3 text-xs text-white">
          <code>{code}</code>
        </pre>
      );
    }

    const lines = block
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean);

    return lines.map((line, lineIndex) => {
      const key = `${blockIndex}-${lineIndex}`;

      if (line.startsWith('### ')) {
        return (
          <h3 key={key} className="mt-3 text-base font-semibold first:mt-0">
            {renderInlineContent(line.slice(4))}
          </h3>
        );
      }

      if (line.startsWith('#### ')) {
        return (
          <h4 key={key} className="mt-2 text-sm font-semibold">
            {renderInlineContent(line.slice(5))}
          </h4>
        );
      }

      if (line.startsWith('- ') || line.startsWith('* ')) {
        return (
          <div key={key} className="ml-4 flex gap-2">
            <span>-</span>
            <span>{renderInlineContent(line.slice(2))}</span>
          </div>
        );
      }

      if (/^\d+\.\s/.test(line)) {
        return (
          <div key={key} className="ml-4">
            {renderInlineContent(line)}
          </div>
        );
      }

      if (line === '---') {
        return <hr key={key} className="my-3 border-gray-300" />;
      }

      return (
        <p key={key} className="mt-2 first:mt-0">
          {renderInlineContent(line)}
        </p>
      );
    });
  });
};

const Chatbot = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(false);

  const handleSendMessage = async (prompt: string, files: File[] = []) => {
    if (!prompt.trim() && files.length === 0) return;

    const trimmedPrompt = prompt.trim();
    const requestPrompt =
      trimmedPrompt || `Please read the attached ${files.length === 1 ? 'file' : 'files'} and summarize the key information.`;

    const userMessage: Message = {
      type: 'user',
      content: trimmedPrompt || `Attached: ${files.map((file) => file.name).join(', ')}`,
    };

    setMessages((prev) => [...prev, userMessage]);

    try {
      setLoading(true);

      const formData = new FormData();

      formData.append('prompt', requestPrompt);

      if (files.length === 1) {
        formData.append('file', files[0]);
      }

      if (files.length > 1) {
        files.forEach((file) => {
          formData.append('files', file);
        });
      }

      const response = await submitContent(formData);

      setMessages((prev) => [
        ...prev,
        {
          type: 'bot',
          content: response.response,
        },
      ]);
    } catch (error) {
      console.error(error);

      setMessages((prev) => [
        ...prev,
        {
          type: 'bot',
          content: 'Something went wrong.',
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col h-full bg-white rounded-xl p-8">
      {messages.length === 0 ? (
        <WelcomeSection />
      ) : (
        <div className="flex-1 overflow-y-auto space-y-4 mb-6">
          {messages.map((message, index) => (
            <div key={index} className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div
                className={`max-w-[80%] rounded-xl px-4 py-3 ${
                  message.type === 'user' ? 'bg-teal-500 text-white' : 'bg-gray-100 text-gray-800'
                }`}
              >
                {message.type === 'bot' ? renderBotContent(message.content) : message.content}
              </div>
            </div>
          ))}

          {loading && <div className="bg-gray-100 inline-block px-4 py-3 rounded-xl">Thinking...</div>}
        </div>
      )}

      <ChatInput onSend={handleSendMessage} loading={loading} />
    </div>
  );
};

export default Chatbot;
